import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatPence, type RosterRow } from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import type { StaffSession } from '../lib/session.js';
import { Button, Card, Banner } from '../components/ui.js';

/**
 * Who is in the room, and the ability to take somebody out of it.
 *
 * Refunds happen away from the app -- cash out of the till, or a card refund in
 * the provider's dashboard -- so the flow is: hand the money back, then look the
 * player up here and eject them. This screen deliberately does not claim to have
 * moved any money; the ticket and the takings are untouched.
 */
export function PlayersTab({ staff }: { staff: StaffSession }) {
  const [players, setPlayers] = useState<RosterRow[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [search, setSearch] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [confirming, setConfirming] = useState<RosterRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ players: RosterRow[]; activeCount: number }>('/api/staff/roster', {
        token: staff.token,
      });
      setPlayers(res.players);
      setActiveCount(res.activeCount);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the roster.');
    }
  }, [staff.token]);

  useEffect(() => {
    void load();
    // Two managers may be working the door at once, so keep it fresh.
    const id = setInterval(() => void load(), 5000);
    return () => clearInterval(id);
  }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return players
      .filter((p) => (showRemoved ? true : p.removedAt === null))
      .filter(
        (p) =>
          !needle ||
          p.callSign.toLowerCase().includes(needle) ||
          (p.ticketCode ?? '').toLowerCase().includes(needle),
      );
  }, [players, search, showRemoved]);

  async function remove(player: RosterRow, reason: string) {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/staff/players/${player.playerId}/remove`, {
        method: 'POST',
        token: staff.token,
        body: { reason },
      });
      setConfirming(null);
      setNote(`${player.callSign} removed.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove them.');
    } finally {
      setBusy(false);
    }
  }

  async function restore(player: RosterRow) {
    setBusy(true);
    try {
      await api(`/api/staff/players/${player.playerId}/restore`, {
        method: 'POST',
        token: staff.token,
      });
      setNote(`${player.callSign} is back in.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not put them back.');
    } finally {
      setBusy(false);
    }
  }

  if (confirming) {
    return <ConfirmRemove player={confirming} busy={busy} onCancel={() => setConfirming(null)} onConfirm={remove} />;
  }

  const removedCount = players.length - activeCount;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-baseline justify-between">
          <p className="text-3xl font-bold tabular-nums">{activeCount}</p>
          <p className="text-sm text-muted">
            in the room
            {removedCount > 0 && ` · ${removedCount} removed`}
          </p>
        </div>
      </Card>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Find a name or ticket code"
        className="w-full rounded-xl bg-ink-2 px-4 py-3 outline-none focus:ring-1 focus:ring-sky"
      />

      {removedCount > 0 && (
        <label className="flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={showRemoved}
            onChange={(e) => setShowRemoved(e.target.checked)}
            className="h-4 w-4"
          />
          Show removed players
        </label>
      )}

      {note && <Banner kind="info">{note}</Banner>}
      {error && <Banner kind="error">{error}</Banner>}

      <ul className="space-y-2">
        {visible.map((p) => {
          const removed = p.removedAt !== null;
          return (
            <li key={p.playerId} className={`rounded-xl bg-ink-2 p-3 ${removed ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-3">
                <span className={`flex-1 truncate font-semibold ${removed ? 'line-through' : ''}`}>
                  {p.callSign}
                </span>
                <span className="tabular-nums text-sm text-muted">{p.score} pts</span>
              </div>

              <p className="mt-1 text-xs text-muted">
                {p.ticketCode ? (
                  <>
                    <span className="font-mono">{p.ticketCode}</span>
                    {' · '}
                    {p.source?.toLowerCase()}
                    {p.amountPence !== null && ` · ${formatPence(p.amountPence)}`}
                  </>
                ) : (
                  'free entry'
                )}
                {' · '}
                {p.answered} answered
                {' · '}
                <LastSeen iso={p.lastSeenAt} />
              </p>

              {removed ? (
                <div className="mt-2 flex items-center gap-3">
                  <p className="flex-1 text-xs text-stop">
                    Removed by {p.removedByName ?? 'staff'}
                    {p.removedReason && `: ${p.removedReason}`}
                  </p>
                  <button
                    disabled={busy}
                    onClick={() => void restore(p)}
                    className="shrink-0 text-sm text-sky underline disabled:opacity-40"
                  >
                    Put back in
                  </button>
                </div>
              ) : (
                <button
                  disabled={busy}
                  onClick={() => setConfirming(p)}
                  className="mt-2 text-sm text-stop underline disabled:opacity-40"
                >
                  Remove from quiz
                </button>
              )}
            </li>
          );
        })}
        {visible.length === 0 && (
          <li className="py-8 text-center text-muted">
            {search ? 'Nobody by that name.' : 'Nobody has joined yet.'}
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * A separate step with a typed reason. Ejecting somebody from a quiz they paid
 * to enter is not something to do on a mis-tap in a dark room, and the reason is
 * what makes the takings reconcilable at the end of the night.
 */
function ConfirmRemove({
  player,
  busy,
  onCancel,
  onConfirm,
}: {
  player: RosterRow;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (player: RosterRow, reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState(
    player.amountPence ? `Refunded ${formatPence(player.amountPence)}` : 'Left the quiz',
  );

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-xs uppercase tracking-wide text-muted">Remove from the quiz</p>
        <p className="mt-1 text-2xl font-bold">{player.callSign}</p>
        <p className="mt-2 text-sm text-muted">
          {player.ticketCode ? (
            <>
              Ticket <span className="font-mono">{player.ticketCode}</span> &middot;{' '}
              {player.source?.toLowerCase()}
              {player.amountPence !== null && ` · ${formatPence(player.amountPence)}`}
            </>
          ) : (
            'Free entry'
          )}
          {' · '}
          {player.score} points
        </p>

        <label htmlFor="reason" className="mt-4 block text-xs uppercase tracking-wide text-muted">
          Why?
        </label>
        <input
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 200))}
          autoFocus
          className="mt-2 w-full rounded-lg bg-ink px-3 py-3 outline-none ring-1 ring-ink-3 focus:ring-sky"
        />

        <p className="mt-4 rounded-lg bg-flag/15 p-3 text-xs text-flag">
          This does not refund anything. Hand the money back at the till or in
          SumUp first &mdash; this only takes them out of the game. Their ticket
          stays on the books exactly as it is.
        </p>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={busy || reason.trim().length === 0}
          onClick={() => void onConfirm(player, reason.trim())}
        >
          {busy ? 'Removing…' : 'Remove'}
        </Button>
      </div>
    </div>
  );
}

function LastSeen({ iso }: { iso: string }) {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 15) return <span className="text-go">here now</span>;
  if (seconds < 120) return <>last seen {seconds}s ago</>;
  // Anything over a couple of minutes means a phone that has stopped polling --
  // worth seeing, because it usually means they have already left.
  return <span className="text-flag">last seen {Math.round(seconds / 60)}m ago</span>;
}
